import { useState } from 'react';
import { Languages } from 'lucide-react';
import { useDismiss } from '@/core/useDismiss';
import { useSession } from '@/core/session';
import { useI18n, useT, LANGUAGES, type Language } from '@/core/i18n';
import { Menu, MenuItem } from './ui';

/* =============================================================================
 * Switching language
 * -----------------------------------------------------------------------------
 * One implementation, used by both shells.
 *
 * It lived inside MyPortal, which is the employee's own screen — and MyPortal is
 * only rendered for somebody with no permissions beyond marking attendance.
 * Everybody else gets the admin shell, which had no switcher at all. So the
 * office half of the app could be translated and nobody working in it could
 * reach the control that shows them.
 *
 * -----------------------------------------------------------------------------
 * Why it goes through the session
 * -----------------------------------------------------------------------------
 * `setLocale` writes the choice against the person as well as the device.
 * i18n's own `setLanguage` changes the screen and this browser, which is not the
 * same thing: somebody who picks Arabic on a site phone and later signs in on a
 * new handset should not have to pick it again, and on a shared phone the next
 * person's language should arrive with their profile rather than be inherited
 * from whoever used it last.
 * ========================================================================== */

export function LanguageToggle() {
  const { language } = useI18n();
  const { setLocale } = useSession();
  const t = useT();
  const [open, setOpen] = useState(false);
  const ref = useDismiss<HTMLDivElement>(open, () => setOpen(false));

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('common.language')}
        className="p-2 rounded-card text-subtle hover:text-text hover:bg-surface-2 transition-colors"
      >
        <Languages size={17} />
      </button>

      {open && (
        <Menu className="w-36">
          {LANGUAGES.map((option) => (
            <MenuItem
              key={option.code}
              active={language === option.code}
              onClick={() => { setLocale(option.code as Language); setOpen(false); }}
            >
              {/* Each language named in its own script. An entry reading
                  "Arabic" in English is the one line on this menu that somebody
                  who reads only Arabic could not use. */}
              {option.nativeLabel}
            </MenuItem>
          ))}
        </Menu>
      )}
    </div>
  );
}
